import { Controller } from "@hotwired/stimulus"

// Assignment picker: enforces XOR between Groups (radio) and
// Individuals (checkboxes).
//
// Three modes:
//   - new-task form: no autosaveUrl, no defer. Parent reads
//     currentValue() on submit.
//   - row-level autosave (legacy, not currently used): autosaveUrl set,
//     defer=false. PATCHes on every change.
//   - panel mode: autosaveUrl set, defer=true. Buffers changes;
//     dispatches panel:dirty on change. Listens for panel:save-all to
//     flush and panel:cancel-all to revert. This is required because
//     autosaving on first checkbox click made multi-individual
//     selection impossible in the panel.
export default class extends Controller {
  static targets = ["groupRadio", "userCheckbox"]
  static values = { autosaveUrl: String, defer: Boolean }

  connect() {
    if (this.deferValue) {
      this.originalGroup = this._currentGroup()
      this.originalUserIds = this._currentUserIds().slice()
      this.wasDirty = false
      this.boundSaveAll = () => this.flushNow()
      this.boundCancelAll = () => this.revert()
      document.addEventListener("panel:save-all", this.boundSaveAll)
      document.addEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  disconnect() {
    if (this.deferValue) {
      document.removeEventListener("panel:save-all", this.boundSaveAll)
      document.removeEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  // Picking a group ticks exactly its interns (each checkbox lists its
  // groups in data-groups), so one can then be unticked (Amanda
  // 2026-09-28). "None" leaves the boxes as they are.
  onGroupChange(event) {
    const key = event.currentTarget.value
    if (key) {
      this.userCheckboxTargets.forEach((cb) => {
        cb.checked = (cb.dataset.groups || "").split(" ").includes(key)
      })
    }
    this.afterChange()
  }

  // Ticking or unticking anyone makes the selection hand-picked: the group
  // goes to "None" and the ticked interns are what gets saved.
  onUserChange() {
    this.groupRadioTargets.forEach((r) => { r.checked = (r.value === "") })
    this.afterChange()
  }

  afterChange() {
    if (this.deferValue) {
      const isDirty = this._isDirty()
      if (isDirty !== this.wasDirty) {
        this.wasDirty = isDirty
        document.dispatchEvent(new CustomEvent("panel:dirty", {
          detail: { dirty: isDirty }
        }))
      }
      return
    }
    this.maybeAutosave()
  }

  // Read-only API for the new-task form: returns the current selection
  // shape so the form can build the right POST payload.
  //   { kind: "none" }
  //   { kind: "group", value: "bmt_interns" }
  //   { kind: "user", value: 398 }
  //   { kind: "users", value: [398, 431] }
  currentValue() {
    const selectedGroup = this.groupRadioTargets.find((r) => r.checked && r.value)
    if (selectedGroup) return { kind: "group", value: selectedGroup.value }
    const selectedUsers = this._currentUserIds()
    if (selectedUsers.length === 0) return { kind: "none" }
    if (selectedUsers.length === 1) return { kind: "user", value: selectedUsers[0] }
    return { kind: "users", value: selectedUsers }
  }

  // Panel mode revert: restore the checkbox/radio state to whatever
  // was active on connect().
  revert() {
    this.groupRadioTargets.forEach((r) => {
      r.checked = (r.value === (this.originalGroup || ""))
    })
    this.userCheckboxTargets.forEach((cb) => {
      cb.checked = this.originalUserIds.includes(parseInt(cb.value, 10))
    })
    this.wasDirty = false
  }

  _currentGroup() {
    const selected = this.groupRadioTargets.find((r) => r.checked && r.value)
    return selected ? selected.value : null
  }

  _currentUserIds() {
    return this.userCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => parseInt(cb.value, 10))
  }

  _isDirty() {
    if (this._currentGroup() !== this.originalGroup) return true
    const now = this._currentUserIds().slice().sort()
    const orig = this.originalUserIds.slice().sort()
    if (now.length !== orig.length) return true
    return now.some((id, i) => id !== orig[i])
  }

  // Panel save-all flush. Auto-save flush. Same network shape either way.
  async flushNow() {
    return this.maybeAutosave()
  }

  async maybeAutosave() {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    // Defer mode: only flush if dirty.
    if (this.deferValue && !this._isDirty()) return
    const v = this.currentValue()
    let stringValue
    switch (v.kind) {
      case "none":  stringValue = ""; break
      case "group": stringValue = `group:${v.value}`; break
      case "user":  stringValue = `user:${v.value}`; break
      case "users": stringValue = `users:${v.value.join(",")}`; break
    }
    document.dispatchEvent(new CustomEvent("panel:saving"))
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify({ field: "assignment", value: stringValue })
      })
      if (!response.ok) throw new Error()
      const data = await response.json().catch(() => ({}))
      document.dispatchEvent(new CustomEvent("panel:saved"))
      // Update baseline so subsequent dirty checks compare against the
      // newly-saved state (no-op if the panel reloads after save).
      this.originalGroup = this._currentGroup()
      this.originalUserIds = this._currentUserIds().slice()
      if (data.reload) window.location.reload()
    } catch (err) {
      console.error("assignment autosave failed:", err)
      document.dispatchEvent(new CustomEvent("panel:save-error"))
    }
  }
}
